import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2, Calendar, Clock, RefreshCw, ExternalLink, Phone, FileText, MapPin, Link as LinkIcon } from 'lucide-react';
import api from '../lib/api';
import { fetchEvent, deleteEvent, eventTypeMeta, RECURRENCE_OPTIONS } from '../lib/events';

export default function EventDetail() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([fetchEvent(eventId), api.get('/family/members').then((r) => r.data.members || [])])
            .then(([e, m]) => { setEvent(e); setMembers(m); })
            .catch((err) => setError(err?.response?.data?.detail || 'Error'))
            .finally(() => setLoading(false));
    }, [eventId]);

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar este evento? Esta acción no se puede deshacer.')) return;
        try {
            await deleteEvent(eventId);
            navigate('/agenda', { replace: true });
        } catch (e) {
            alert(e?.response?.data?.detail || 'Error');
        }
    };

    if (loading) return <div className="min-h-screen bg-surface flex items-center justify-center"><div className="w-10 h-10 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>;
    if (error || !event) return <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6"><p className="text-sm text-red-600 mb-4">{error || 'Evento no disponible'}</p><button onClick={() => navigate('/agenda')} className="text-brand font-bold">Volver a Agenda</button></div>;

    const meta = eventTypeMeta(event.type);
    const notified = (event.notify_members || []).map((id) => members.find((m) => m.id === id)).filter(Boolean);

    return (
        <div className="min-h-screen bg-surface app-shell pb-10">
            {event.image_url ? (
                <div className="relative h-44 bg-gray-200">
                    <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                    <button onClick={() => navigate(-1)} className="absolute top-4 left-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center" data-testid="event-back"><ArrowLeft className="w-5 h-5 text-brand-navy" /></button>
                    <button onClick={() => navigate(`/agenda/${event.id}/editar`)} className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center" data-testid="event-edit"><Pencil className="w-4 h-4 text-brand-navy" /></button>
                </div>
            ) : (
                <div className="px-6 pt-4 flex items-center justify-between">
                    <button onClick={() => navigate(-1)} className="w-10 h-10 -ml-2 hover:bg-gray-100 rounded-full flex items-center justify-center" data-testid="event-back"><ArrowLeft className="w-5 h-5 text-brand-navy" /></button>
                    <h1 className="font-bold text-brand-navy">Detalle del Evento</h1>
                    <div className="flex gap-1">
                        <button onClick={() => navigate(`/agenda/${event.id}/editar`)} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="event-edit"><Pencil className="w-4 h-4 text-brand-navy" /></button>
                        <button onClick={handleDelete} className="w-10 h-10 rounded-full hover:bg-red-50 flex items-center justify-center" data-testid="event-delete"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                </div>
            )}

            <div className="px-6 mt-5">
                <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${meta.color}`} data-testid="event-type-label">{meta.label}</span>
                <h1 className="mt-3 text-2xl font-bold text-brand-navy" data-testid="event-title">{event.title}</h1>

                <div className="mt-4 bg-white rounded-2xl shadow-card p-4 space-y-3">
                    <Row icon={Calendar} title={formatLongDate(event.date)} subtitle="Fecha del evento" />
                    {event.time && <Row icon={Clock} title={`${event.time} hs`} subtitle="Hora de inicio" />}
                    {event.recurrence && (
                        <Row icon={RefreshCw} title={formatRecurrence(event.recurrence)} subtitle="Evento recurrente" iconClass="text-fuchsia-500" />
                    )}
                </div>

                {event.description && (
                    <div className="mt-5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Nota</h3>
                        <div className="bg-white rounded-2xl shadow-card p-4">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed" data-testid="event-description">{event.description}</p>
                        </div>
                    </div>
                )}

                {event.address && (
                    <div className="mt-5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Dirección</h3>
                        <div className="bg-white rounded-2xl shadow-card p-4">
                            <p className="text-sm text-gray-800">{event.address}</p>
                            <a href={`https://maps.google.com/?q=${encodeURIComponent(event.address)}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-brand text-sm font-bold" data-testid="event-maps">
                                Abrir en Maps <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    </div>
                )}

                {(event.links || []).length > 0 && (
                    <div className="mt-5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2"><LinkIcon className="w-3.5 h-3.5" /> Enlaces</h3>
                        <div className="space-y-2">
                            {event.links.map((l, i) => (
                                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-white rounded-2xl shadow-card p-3" data-testid={`event-link-${i}`}>
                                    <div className="flex items-center gap-3">
                                        <span className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center"><LinkIcon className="w-4 h-4 text-blue-500" /></span>
                                        <span className="text-sm font-semibold text-brand-navy">{l.label || l.url}</span>
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-gray-400" />
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {(event.contacts || []).length > 0 && (
                    <div className="mt-5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> Contactos</h3>
                        <div className="space-y-2">
                            {event.contacts.map((c, i) => (
                                <a key={i} href={c.phone ? `tel:${c.phone}` : undefined} className="flex items-center justify-between bg-white rounded-2xl shadow-card p-3" data-testid={`event-contact-${i}`}>
                                    <div className="flex items-center gap-3">
                                        <span className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold text-sm">{(c.name || '?').charAt(0).toUpperCase()}</span>
                                        <div>
                                            <div className="text-sm font-bold text-brand-navy">{c.name}</div>
                                            <div className="text-xs text-gray-500">{c.phone}</div>
                                        </div>
                                    </div>
                                    {c.phone && <Phone className="w-4 h-4 text-brand" />}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {(event.documents || []).length > 0 && (
                    <div className="mt-5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Documentos</h3>
                        <div className="space-y-2">
                            {event.documents.map((d, i) => (
                                <div key={i} className="flex items-center justify-between bg-white rounded-2xl shadow-card p-3">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center"><FileText className="w-4 h-4 text-red-500" /></span>
                                        <div className="min-w-0">
                                            <div className="text-sm font-bold text-brand-navy truncate">{d.name}</div>
                                            {d.size && <div className="text-xs text-gray-500">{Math.round(d.size / 1024)} KB</div>}
                                        </div>
                                    </div>
                                    {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-brand font-bold text-sm">Abrir</a>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {notified.length > 0 && (
                    <div className="mt-5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Notificados</h3>
                        <div className="flex flex-wrap gap-2">
                            {notified.map((m) => (
                                <span key={m.id} className="px-3 h-8 inline-flex items-center bg-white rounded-full shadow-card text-sm font-semibold text-brand-navy" data-testid={`event-notified-${m.id}`}>{m.name?.split(' ')[0] || m.email}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ icon: Icon, title, subtitle, iconClass = 'text-brand' }) {
    return (
        <div className="flex items-center gap-3">
            <span className={`w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center ${iconClass}`}><Icon className="w-5 h-5" /></span>
            <div>
                <div className="text-sm font-bold text-brand-navy">{title}</div>
                <div className="text-xs text-gray-500">{subtitle}</div>
            </div>
        </div>
    );
}

function formatLongDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatRecurrence(r) {
    const freqLabel = RECURRENCE_OPTIONS.find((o) => o.value === r.frequency)?.label || r.frequency;
    const interval = r.interval || 1;
    let base;
    if (r.frequency === 'daily') base = interval === 1 ? 'Cada día' : `Cada ${interval} días`;
    else if (r.frequency === 'weekly') {
        base = interval === 1 ? 'Cada semana' : `Cada ${interval} semanas`;
        if ((r.days_of_week || []).length > 0) {
            const names = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
            const days = [...r.days_of_week].sort().map((i) => names[i]).join(', ');
            base += ` · ${days}`;
        }
    } else if (r.frequency === 'monthly') base = interval === 1 ? 'Cada mes' : `Cada ${interval} meses`;
    else if (r.frequency === 'yearly') base = interval === 1 ? 'Cada año' : `Cada ${interval} años`;
    else base = freqLabel;
    if (r.until) base += ` (hasta ${r.until})`;
    return base;
}
